/**
 * フォーム機能（Phase 24 / spec_line_bot.md A-12）
 * 公開フォームと回収データのデータモデル。
 *
 * 2026-04-25 拡張（Phase 24b）:
 *   - FormElement union（field / title / body / image / divider）
 *   - publicStartAt / publicEndAt（公開期間）
 *   - 期間外は自動で 'closed' に遷移（closeExpiredForms cron）
 */
import type { DepartmentId } from '../constants';
import type { IsoOrTimestamp } from './common';

// ─────────────────────────────────────────────
// 入力フィールド型
// ─────────────────────────────────────────────
export type FormFieldType =
  | 'text'
  | 'textarea'
  | 'email'
  | 'tel'
  | 'number'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'date'
  | 'consent'
  // 返答要求 (2026-07-23 追加)
  // 回答者が「返答がほしい」ことをチェックボックスで表明するフィールド。値は 'true' / ''。
  // チェック済み ('true') の回収データは回収一覧で「返答待ち」ラベル対象になり、
  // sendFormReplyMail Callable での返信送信 (replySentAt 書込) で「返答済み」に変わる。
  | 'reply_request'
  // 顧客標準フィールド (2026-06-14 追加)
  // ラベル文字列の推測ではなく fieldType で MailContact の正規枠へ確定マッピングする semantic フィールド。
  // onFormSubmissionCreated が以下のとおり mail_contacts へ直接書き込む（customFields には入れない）:
  //   last_name       → MailContact.lastName       (姓)
  //   first_name      → MailContact.firstName      (名)
  //   last_name_kana  → MailContact.lastNameKana   (せい / フリガナ)
  //   first_name_kana → MailContact.firstNameKana  (めい / フリガナ)
  //   phone           → MailContact.phone          (電話番号)
  //   company_name    → MailContact.companyName    (会社名 / 2026-07-28 追加)
  // 姓 + 名 が揃う場合は MailContact.name = `${lastName} ${firstName}` も自動合成する。
  | 'last_name'
  | 'first_name'
  | 'last_name_kana'
  | 'first_name_kana'
  | 'phone'
  | 'company_name'
  // 生年月日 (2026-08-24 追加、顧客情報確認・更新フォーム用)
  //   birthdate → MailContact.birthdate (YYYY-MM-DD / 西暦)
  // 公開フォームは input type=date で収集。onFormSubmissionCreated が
  // YYYY-MM-DD 形式に正規化できた値のみ反映する (不正値は customFields にも入れず破棄)。
  | 'birthdate'
  // 構造化住所フィールド (2026-05-28 追加)
  // 1 質問で 郵便番号 / 都道府県 / 市区町村 / 町名・番地・建物 をまとめて入力する。
  // 回答値は `${postalCode}|${prefecture}|${city}|${line}` のパイプ区切り文字列で
  // FormSubmission.values に格納される (検索容易性のため文字列化)。
  // onFormSubmissionCreated が分解して MailContact.billingAddresses[] / deliveryAddresses[] へ
  // 反映する。2026-07-28 以降は normalizeAddressKey の一致で
  // 「既存住所の更新」/「別住所として追加 + 既定に昇格」を自動判定する。
  | 'billing_address'           // 請求先住所
  | 'delivery_address'          // 納品先住所
  // 英表記登録情報 (2026-07 追加)
  // 1 質問で 英語名 / 国番号付き電話 / 国 / 英語住所(国別フィールド) をまとめて入力する。
  // 回答値は encodeEnglishProfile() のパイプ区切り文字列 (順序は ENGLISH_PROFILE_FIELD_ORDER)。
  // onFormSubmissionCreated が分解して MailContact.nameEn / phoneIntl / englishAddress へ
  // 書き込む (単数枠のため上書き設定に従う)。
  | 'english_profile'
  // 営業フォーム用 動的フィールド (Phase 1+) — 公開フォームで runtime に候補を取得する
  | 'briefing_schedule_picker'  // briefing_schedules から取得 (個別説明申込)
  | 'product_picker'            // products_list の有効期間内 products[] から取得 (見積依頼)
  | 'assignee_picker';          // admins/{uid} role IN ('editor','owner') から取得 (見積依頼)

// ─────────────────────────────────────────────
// FormElement union（質問 + レイアウト要素）
// ─────────────────────────────────────────────
export interface BaseFormElement {
  id: string;
}

/** 入力フィールド（回答対象） */
export interface FieldFormElement extends BaseFormElement {
  kind: 'field';
  fieldType: FormFieldType;
  label: string;
  placeholder?: string;
  required: boolean;
  /** select / radio / checkbox 用 */
  options?: string[];
  /**
   * checkbox（複数選択）の選択上限数 (2026-08-31 追加)。
   * 未設定 / undefined なら上限なし。checkbox 以外では無視される。
   *   - 公開フォームは上限に達すると未選択の項目を disabled にし「上限に達しました」を表示
   *   - required の checkbox は「上限数ちょうど」を満たさないと送信できない
   *     (required でなければ上限未満でも送信可)
   * 実効値は resolveMaxSelections() で正規化する（選択肢数を超える指定は選択肢数に丸める）。
   */
  maxSelections?: number;
  helpText?: string;
  /** consent 用 */
  consentText?: string;
  /**
   * briefing_schedule_picker / product_picker 用の追加フィルタ。
   * - briefing_schedule_picker: そのフォーム独自の参加スタイル指定 (online/onsite/both)
   * - product_picker: 主軸カテゴリ指定 (Form.primaryCategoryId と異なる場合のみ意味あり、通常は不要)
   */
  filterStyle?: 'online' | 'onsite' | 'both';
  filterPrimaryCategoryId?: string;
}

/** タイトル見出し（h1〜h3） */
export interface TitleFormElement extends BaseFormElement {
  kind: 'title';
  text: string;
  level: 'h1' | 'h2' | 'h3';
}

/** 本文段落（説明テキスト） */
export interface BodyFormElement extends BaseFormElement {
  kind: 'body';
  text: string;
}

/** 画像セクション */
export interface ImageFormElement extends BaseFormElement {
  kind: 'image';
  imageUrl: string;
  alt?: string;
  width?: number;
  height?: number;
}

/** 区切り線 */
export interface DividerFormElement extends BaseFormElement {
  kind: 'divider';
}

export type FormElement =
  | FieldFormElement
  | TitleFormElement
  | BodyFormElement
  | ImageFormElement
  | DividerFormElement;

export type FormElementKind = FormElement['kind'];

// ─────────────────────────────────────────────
// checkbox（複数選択）の選択上限 (2026-08-31 追加)
// 編集画面プレビュー / 公開フォーム / submitForm の 3 箇所で同じ判定を使うため、
// 正規化と検証をここに一本化する。
// ─────────────────────────────────────────────

/**
 * checkbox の実効的な選択上限を返す。上限なしなら undefined。
 *   - checkbox 以外 / 未設定 / 1 未満 / 非数値 → undefined（上限なし）
 *   - 選択肢の数を超える指定 → 選択肢の数に丸める
 *     （必須 + 上限が選択肢数を超えると絶対に送信できなくなるため）
 */
export function resolveMaxSelections(
  field: Pick<FieldFormElement, 'fieldType' | 'maxSelections' | 'options'>,
): number | undefined {
  if (field.fieldType !== 'checkbox') return undefined;
  const raw = field.maxSelections;
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return undefined;
  const max = Math.floor(raw);
  if (max < 1) return undefined;
  const optionCount = field.options?.length ?? 0;
  return optionCount > 0 ? Math.min(max, optionCount) : max;
}

/** 回答値を選択済みラベルの配列に正規化する（文字列 / undefined も許容）。 */
export function toSelectedOptions(
  value: string | string[] | undefined,
): string[] {
  if (Array.isArray(value)) {
    return value.filter((v) => typeof v === 'string' && v.trim() !== '');
  }
  if (typeof value === 'string' && value.trim() !== '') return [value.trim()];
  return [];
}

/**
 * checkbox の選択数を検証する。問題なければ null、あれば表示用メッセージ。
 *   - 上限超過は常にエラー（公開エンドポイントなので改竄された配列も弾く）
 *   - required なら「上限数ちょうど」を要求する
 *   - required でなければ上限未満は OK（未選択は呼び出し側の必須チェックに委ねる）
 */
export function validateCheckboxSelection(
  field: FieldFormElement,
  value: string | string[] | undefined,
): string | null {
  const max = resolveMaxSelections(field);
  if (max === undefined) return null;
  const count = toSelectedOptions(value).length;
  if (count > max) return `「${field.label}」は最大 ${max} つまで選択できます`;
  if (field.required && count < max) {
    return `「${field.label}」は ${max} つ選択してください`;
  }
  return null;
}

// ─────────────────────────────────────────────
// テーマ
// ─────────────────────────────────────────────
export interface FormTheme {
  brandColor?: string;
  /** フォームカード（入力欄が乗る箱）の背景色。未設定時は白 */
  backgroundColor?: string;
  /** ロゴ画像 URL（storage/system/imgs/ 経由）*/
  logoUrl?: string;
}

// ─────────────────────────────────────────────
// フォーム本体
// ─────────────────────────────────────────────
export type FormStatus = 'draft' | 'published' | 'closed';

/**
 * フォームの種別タグ (Phase 1+)。
 * - 'marketing': LP問合せ・メルマガ登録・アンケート等。tag 未設定の既存フォームは marketing 扱い。
 * - 'sales': 営業フローに直結 (資料請求 / 個別説明申込 / 見積依頼 / 顧客情報確認・更新)。
 *   送信時に Deal が作られる (例外: category='customer_info' は Deal を作らない)。
 */
export type FormTag = 'marketing' | 'sales';

/**
 * 営業フォームの送信時の Deal Stage ヒント (sales tag のみで使用)。
 * resolveDealForSubmission が参照し、新規 or 既存 Deal の stage を決定する。
 */
export type DealStageHint = 'document_requested' | 'briefing' | 'quote';

/**
 * 営業フォームのカテゴリ (sales tag のみで使用)。dealStageHint と1:1対応で UI 自動派生する想定。
 * - 'document_download' ↔ 'document_requested'
 * - 'explanation'       ↔ 'briefing'
 * - 'quote'             ↔ 'quote'
 * - 'customer_info'     ↔ dealStageHint なし (2026-08-24 追加)
 *   顧客情報確認・更新フォーム。住所・英表記等の顧客マスタ更新が目的で、
 *   送信しても Deal の自動作成・紐付けは行わない (resolveDealForSubmission が明示 skip)。
 */
export type FormCategory =
  | 'document_download'
  | 'explanation'
  | 'quote'
  | 'customer_info';

/**
 * フォーム送信で顧客 (mail_contacts) の単数枠をどう扱うか (2026-07-28 追加)。
 * 詳細は Form.contactOverwritePolicy を参照。
 */
export type ContactOverwritePolicy = 'fill' | 'overwrite';

export interface Form {
  /** 管理画面表示用 */
  name: string;
  /** 公開ページのタイトル */
  title: string;
  description?: string;
  /** 全要素（質問 + レイアウト） */
  elements: FormElement[];
  /** 送信完了メッセージ */
  thanksMessage?: string;
  /** 送信完了時のリダイレクト URL（任意） */
  redirectUrl?: string;
  theme?: FormTheme | null;
  status: FormStatus;
  /** 公開開始時刻（null なら制限なし） */
  publicStartAt?: IsoOrTimestamp | null;
  /** 公開終了時刻（null なら制限なし、超過すると closeExpiredForms で自動 'closed'） */
  publicEndAt?: IsoOrTimestamp | null;
  /** 紐付くキャンペーン（複数可、Phase 23 連動） */
  campaignIds: string[];
  /**
   * 送信者の mail_contacts.tags に追加するタグ（Phase 24e）。
   * 空または未指定のときは tags への自動マージなし。
   * 既存タグからの選択 + 新規入力の両方を想定。
   */
  contactTags?: string[];
  /**
   * 送信内容で顧客の「単数枠」を上書きするか (2026-07-28 追加)。
   *
   * 対象は 1 つしか値を持てない枠だけ:
   *   氏名 (name / lastName / firstName / カナ) / phone / companyName /
   *   nameEn / phoneIntl / englishAddress
   *
   * - 'fill' (既定 / 未設定時): 既存値が空の枠だけ補完する。既存値は保護される。
   * - 'overwrite': 空でない回答が来たら既存値を上書きする。
   *   住所変更フォーム・英文住所フォームなど「最新の申告で顧客マスタを更新したい」用途向け。
   *
   * 複数枠 (emails[] / billingAddresses[] / deliveryAddresses[]) はこの設定の対象外で、
   * 常に「同一なら更新 / 別物なら追加して既定に昇格」で処理される。
   *
   * なお 'overwrite' でも、顧客が氏名一致のみで同定された送信 (trust='weak') では
   * 同姓同名の別人による破壊を避けるため上書きは行わない (fill にフォールバック)。
   */
  contactOverwritePolicy?: ContactOverwritePolicy;
  /** 回収件数（denormalize、submitForm Callable で増分） */
  submissionCount: number;

  // ─── 定員制限（2026-05-20 追加） ───
  /**
   * 受付上限件数。
   *   null / undefined → 無制限
   *   0 → 即「定員に達しました」表示（フォーム一時停止用）
   *   正の整数 → submissionCount がこの値に達した時点で受付停止
   * Firestore トランザクションで submissionCount と同一トランザクションでチェック・更新する。
   */
  capacity?: number | null;
  /** 定員到達時の公開フォーム表示メッセージ（未設定なら「定員に達しました」） */
  capacityFullMessage?: string;
  /** 残枠数（capacity - submissionCount）を公開フォームに表示するか */
  showRemainingCount?: boolean;

  // ─── 営業ツール (Phase 1+) ───
  /**
   * フォームの種別タグ。
   * 未設定 (undefined) は後方互換で 'marketing' 扱い。
   * backfillFormTags Callable で全件補完可能。
   */
  tag?: FormTag;
  /** sales tag のみで意味あり。送信時の Deal Stage 確定に使う。 */
  dealStageHint?: DealStageHint | null;
  /**
   * 主軸カテゴリ表示名 (例: "プロ機器" / "コンシューマ機器")。
   * 表示用スナップショット。真のソースは primaryCategoryId。
   */
  primaryCategory?: string | null;
  /**
   * v3.1.3: 主軸カテゴリの ID 参照 (`product_categories/{id}`)。
   * 名称変更を関連フォームに反映するため、ID 参照を真のソースとする。
   */
  primaryCategoryId?: string | null;
  /**
   * Phase A: フォームにあらかじめ紐付ける商品 (任意)。
   * 見積依頼フォームの product_picker のフィルタや、Deal 作成時の primaryProductIds の初期値に使う。
   */
  relatedProductIds?: string[];
  /** sales tag のみ。dealStageHint から自動派生 (UI で算出して保存)。 */
  category?: FormCategory | null;
  /**
   * Phase 8: dealStageHint='document_requested' の form でのみ意味あり。
   * 設定されている場合、フォーム送信時に該当 documents/{documentId} の公開URL +
   * パスワードを記載した自動返信メールが顧客に送られる。
   */
  documentId?: string | null;
  /**
   * briefing_schedule_picker で `?sa=` が null / 無効 のときに表示する
   * 枠の担当者所属 dept を絞る。`?sa=` 有効時はこの設定は無視され SA の枠のみ表示。
   * 未設定 (undefined / null) は後方互換で「全 dept (level=external 以外)」扱い。
   * 新規フォームのデフォルトは `['sales']`（営業部の社内メンバーのみ）を推奨。
   */
  briefingDepartmentFilter?: DepartmentId[] | null;
  /**
   * 弊社紹介フラグ (v3.2.29+)。
   * true のとき、このフォームから作成された Deal は `Deal.isCompanyReferral=true` で起票される。
   * 弊社が SA にお客様を紹介する「個別説明依頼」用のフォームに ON する想定。
   * SA の報酬計算時に commission_sets.companyReferralSaDiscountRates[primaryCategoryId] の率で減額される。
   */
  isCompanyReferral?: boolean;

  /**
   * SA ポータル `/sa/forms` での表示可否 (v3.2.28+)。
   *   - true / undefined: 表示 (既定、後方互換)
   *   - false           : 非表示
   * SA に必要なフォームのみを見せて視覚的負荷を下げる目的。
   * 営業フォーム (`tag === 'sales'`) / マーケティングフォーム (`tag === 'marketing'`) どちらも対象。
   */
  visibleToSa?: boolean;

  /**
   * 完了通知 (2026-06-18 追加)。
   * フォーム送信完了時、回答者本人のメールアドレス宛に「回答内容の控え」を自動送信する設定。
   * enabled=true のとき onFormSubmissionCreated が completion メールを送る。
   * その場合、既存の form_submitted 業務通知（サンクスメール）は抑制される（二重送信防止）。
   */
  completionNotification?: FormCompletionNotification | null;

  createdBy: string;
  createdAt: IsoOrTimestamp;
  updatedAt: IsoOrTimestamp;
}

// ─────────────────────────────────────────────
// 完了通知（回答者本人への控えメール）
// ─────────────────────────────────────────────
export interface FormCompletionNotification {
  /** ON/OFF。false / 未設定 のときは送信しない。 */
  enabled: boolean;
  /**
   * 送信元 mail_accounts/{id}。
   * 空文字 / null / 未設定 = システム既定アカウント（resolveAutoMailAccountId が自動解決）。
   */
  fromAccountId?: string | null;
  /** CC 宛先（管理者の控え等）。小文字化したメールアドレス配列。 */
  cc?: string[];
  /** 件名 */
  subject: string;
  /** 本文（01）。回答ブロックの前に表示。 */
  body: string;
  /** URL リンク（02）。空 / null のときは本文生成時にスキップ。 */
  url?: string | null;
  /**
   * 本文に含める回答のフィールド ID 配列。
   *   null / undefined = 全フィールド（既定。後から追加した質問も自動的に含む）
   *   配列            = 指定 ID の回答のみ表示
   */
  includedFieldIds?: string[] | null;
}

// ─────────────────────────────────────────────
// 完了通知メール本文の生成（admin プレビューと functions 送信で共用）
// 構成: 01 本文 → 02 URL（無ければスキップ）→ 回答 → フッター
// ─────────────────────────────────────────────
export interface CompletionMailAnswerLine {
  label: string;
  value: string;
}

/**
 * product_picker の回答1件分 (公開フォームは JSON.stringify した配列を values に保存する)。
 * FormElementRenderer / functions 側と同形。
 */
export interface ProductPickerAnswerEntry {
  /** 選択元カタログ (product_catalogs/{id}) */
  productListId: string;
  modelName: string;
  variation?: string | null;
  qty?: number;
}

/** product_picker の回答 JSON 文字列をパースする。壊れていれば空配列。 */
export function parseProductPickerAnswer(
  raw: string | null | undefined,
): ProductPickerAnswerEntry[] {
  if (typeof raw !== 'string' || !raw.trim()) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is ProductPickerAnswerEntry =>
        !!e &&
        typeof (e as ProductPickerAnswerEntry).productListId === 'string' &&
        typeof (e as ProductPickerAnswerEntry).modelName === 'string',
    );
  } catch {
    return [];
  }
}

/** product_picker 回答の数量を 1〜 の整数へ正規化。 */
export function normalizeProductPickerQty(qty: unknown): number {
  return typeof qty === 'number' && Number.isFinite(qty) && qty >= 1
    ? Math.floor(qty)
    : 1;
}

/**
 * product_picker の回答 JSON を「商品名 × 個数」の読める表記へ整形する。
 * カタログ参照なしの同期フォールバック (modelName ベース)。functions 側は
 * カタログから顧客向け商品名 (name) を解決した valueLabels を優先して使う。
 * パースできない場合は raw をそのまま返す (情報を落とさない)。
 */
export function formatProductPickerAnswer(raw: string): string {
  const entries = parseProductPickerAnswer(raw);
  if (entries.length === 0) return raw;
  return entries
    .map((e) => {
      const name = `${e.modelName}${e.variation ? ` (${e.variation})` : ''}`;
      return `${name} × ${normalizeProductPickerQty(e.qty)}`;
    })
    .join('、');
}

/**
 * フォーム回答 1 件を表示用文字列に整形（住所・複数選択・同意を考慮）。
 * valueLabels: 生の回答値 → 表示ラベルの置換マップ (2026-07-24 追加)。
 * briefing_schedule_picker の枠 id を「2026-07-28 11:00〜12:00 (オンライン)」等の
 * 読める表記へ変換する用途 (呼び出し側が Firestore から解決して渡す)。
 */
function formatCompletionAnswerValue(
  field: FieldFormElement,
  raw: string | string[] | undefined,
  valueLabels?: Record<string, string>,
): string {
  const mapValue = (v: string): string => valueLabels?.[v] ?? v;
  if (raw == null) return '';
  // product_picker は JSON 文字列のため専用整形 (2026-08-05 追加)。
  // functions 側がカタログ解決済みラベルを valueLabels (key=raw全体) で渡していればそれを優先。
  if (field.fieldType === 'product_picker' && typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return '';
    return valueLabels?.[s] ?? formatProductPickerAnswer(s);
  }
  if (Array.isArray(raw)) {
    return raw
      .filter((v) => v && v.trim())
      .map(mapValue)
      .join('、');
  }
  const s = mapValue(String(raw).trim());
  if (!s) return '';
  if (field.fieldType === 'billing_address' || field.fieldType === 'delivery_address') {
    const p = trimAddressParts(parseAddressValue(s));
    return [
      p.postalCode ? `〒${p.postalCode}` : '',
      p.prefecture,
      p.city,
      p.line,
    ]
      .filter((x) => x && x.trim())
      .join(' ');
  }
  if (field.fieldType === 'consent') return s === 'true' ? '同意しました' : s;
  if (field.fieldType === 'reply_request') return s === 'true' ? '希望する' : s;
  return s;
}

/**
 * フォーム要素と回答値から、完了通知に載せる回答行を抽出する。
 * includedFieldIds が配列なら該当 ID のみ、null/undefined なら全フィールド。
 * 値は表示用に整形済み（空値は呼び出し側 buildCompletionMailText で除外される）。
 */
export function extractCompletionAnswers(
  elements: FormElement[],
  values: Record<string, string | string[]> | undefined,
  includedFieldIds?: string[] | null,
  /** 生回答値 → 表示ラベル置換 (枠 id の日程表記化など)。 */
  valueLabels?: Record<string, string>,
): CompletionMailAnswerLine[] {
  const includeSet =
    Array.isArray(includedFieldIds) && includedFieldIds.length >= 0
      ? new Set(includedFieldIds)
      : null;
  const out: CompletionMailAnswerLine[] = [];
  for (const el of elements ?? []) {
    if (el.kind !== 'field') continue;
    const field = el as FieldFormElement;
    if (includeSet && !includeSet.has(field.id)) continue;
    out.push({
      label: field.label || '(無題の質問)',
      value: formatCompletionAnswerValue(field, values?.[field.id], valueLabels),
    });
  }
  return out;
}

/**
 * 完了通知メールの本文（plain text）を組み立てる。
 * 01 本文 → 02 URL（空ならスキップ）→ 回答（空値は除外）→ フッター の順、空ブロックは詰める。
 */
export function buildCompletionMailText(parts: {
  body: string;
  url?: string | null;
  answers: CompletionMailAnswerLine[];
  footer?: string | null;
  /** 回答ブロックの見出し（既定: 「ご回答内容」） */
  answersHeading?: string;
}): string {
  const blocks: string[] = [];
  const body = (parts.body ?? '').trim();
  if (body) blocks.push(body);
  const url = (parts.url ?? '').trim();
  if (url) blocks.push(url);
  const answerLines = (parts.answers ?? [])
    .filter((a) => a.value && a.value.trim())
    .map((a) => `${a.label}: ${a.value}`);
  if (answerLines.length > 0) {
    const heading = parts.answersHeading ?? 'ご回答内容';
    blocks.push([`【${heading}】`, ...answerLines].join('\n'));
  }
  const footer = (parts.footer ?? '').trim();
  if (footer) blocks.push(footer);
  return blocks.join('\n\n');
}

// ─────────────────────────────────────────────
// 回収データ
// ─────────────────────────────────────────────
/**
 * briefing_schedule_picker の参加人数入力の values キー サフィックス (2026-07-23 追加)。
 * 公開フォームは `${fieldId}__participants` キーに人数 (文字列) を入れて送信し、
 * submitForm がパースして FormSubmission.briefingParticipantCount に正規化保存する。
 */
export const BRIEFING_PARTICIPANTS_SUFFIX = '__participants';

/** briefing_schedule_picker フィールドの参加人数 values キーを返す。 */
export function briefingParticipantsKey(fieldId: string): string {
  return `${fieldId}${BRIEFING_PARTICIPANTS_SUFFIX}`;
}

export interface FormSubmission {
  formId: string;
  /** URL ?campaignId=X から抽出 */
  campaignId: string | null;
  /** field.id → 値（複数選択は string[]） */
  values: Record<string, string | string[]>;
  /**
   * 個別説明の参加人数 (2026-07-23 追加)。briefing_schedule_picker のあるフォームで
   * 顧客が入力した人数 (1〜capacity)。capacity=同時参加可能人数。
   * 枠のクローズは人数に依らず1予約で行う (1枠=1予約)。未入力・旧データは undefined (=1名扱い)。
   */
  briefingParticipantCount?: number | null;
  ipAddress?: string;
  userAgent?: string;
  referrer?: string;
  email?: string;
  contactId?: string;
  /**
   * 公開 URL の `?cid=` で渡された顧客 ID (2026-07-28 追加)。
   * メール本文のリンク経由で「本人が開いたフォーム」であることの根拠として、
   * onFormSubmissionCreated が顧客同定の最優先キーに使う (メアドを変更した申告でも
   * 同一顧客に紐付けられる)。contactId は推測不能な発行 ID のため、
   * 見積・請求の公開リンクと同じ扱いで本人性の根拠とする。
   */
  sourceContactId?: string | null;
  /**
   * Phase 24f: LIFF 経由で送信された場合の LINE userId。
   * onFormSubmissionCreated で mail_contacts.lineUid に upsert される。
   */
  lineUid?: string;
  /**
   * Phase 3+: resolveDealForSubmission が書き戻す。
   * sales フォームの場合のみ設定され、対応する Deal の id が入る。
   */
  dealId?: string | null;
  /**
   * v3.2.18 SA リフェラル経由送信時の SA uid。
   * 公開URL `?sa=<uid>` をサーバ側で admins 検証 (dept='sales' && level='external') 後に記録。
   * 検証失敗・未指定時は null。resolveDealForSubmission が Deal.salesAgentUid にコピーする。
   */
  salesAgentUid?: string | null;
  /**
   * 弊社紹介フラグ (v3.2.29+)。
   * 送信元 Form.isCompanyReferral=true の場合に true となり、resolveDealForSubmission が
   * 新規 Deal.isCompanyReferral にコピーする。
   */
  isCompanyReferral?: boolean;
  /**
   * 返答要求 (reply_request) への返信メール送信記録 (2026-07-23 追加)。
   * sendFormReplyMail Callable が送信成功時に書き込む (クライアント直書きは rules で禁止)。
   * 「返答待ち」/「返答済み」ラベルは replySentAt の有無で判定する。
   */
  replySentAt?: IsoOrTimestamp | null;
  replySentTo?: string | null;
  /** 返信を送った管理者 uid */
  replySentBy?: string | null;
  replyMessageId?: string | null;
  /**
   * 返答フラグの手動上書き (2026-08-07 追加)。
   * null / undefined = 回答内容 (reply_request のチェック) に従う。
   * true  = 回答でチェックされていなくても「返答対象」として扱う。
   * false = チェックされていても「返答対象外」として扱う (ラベル非表示)。
   * 書込は updateFormReplyStatus Callable のみ (form_submissions はクライアント update 禁止)。
   */
  replyRequiredOverride?: boolean | null;
  /** 手動でフラグを更新した管理者 uid / 日時。 */
  replyStatusUpdatedBy?: string | null;
  replyStatusUpdatedAt?: IsoOrTimestamp | null;
  /**
   * 返答要求の自動掲示板投稿 (2026-08-07 追加) で作成された board_posts の id。
   * 二重投稿防止のガードも兼ねる (値があれば作成済み)。
   */
  boardPostId?: string | null;
  submittedAt: IsoOrTimestamp;
}

/**
 * 返答要求フィールドが 1 つ以上チェックされた回収データか (2026-07-23 追加)。
 * true のとき回収一覧で「返答待ち」(replySentAt 無) / 「返答済み」(有) ラベルを表示する。
 */
export function isReplyRequested(
  elements: FormElement[] | undefined,
  values: Record<string, string | string[]> | undefined,
): boolean {
  return (elements ?? []).some(
    (e) =>
      e.kind === 'field' &&
      e.fieldType === 'reply_request' &&
      values?.[e.id] === 'true',
  );
}

/**
 * 回収データの返答状態 (2026-08-07 追加)。
 *   null       — 返答対象外 (ラベル非表示)
 *   'awaiting' — 返答待ち  (返信未送信)
 *   'replied'  — 返答済み  (返信メール送信済み / 手動で済みにした)
 *
 * 判定順:
 *   1. replyRequiredOverride (手動上書き) があればそれを優先
 *   2. 無ければ reply_request のチェック状態 (isReplyRequested)
 * 返答対象なら replySentAt の有無で awaiting / replied を分ける。
 */
export function resolveFormReplyStatus(
  elements: FormElement[] | undefined,
  submission: Pick<
    FormSubmission,
    'values' | 'replySentAt' | 'replyRequiredOverride'
  >,
): FormReplyStatus | null {
  const required =
    submission.replyRequiredOverride ??
    isReplyRequested(elements, submission.values);
  if (!required) return null;
  return submission.replySentAt ? 'replied' : 'awaiting';
}

/** 返答状態 (手動更新 Callable のペイロードにも使う)。'none' = 返答対象外。 */
export type FormReplyStatus = 'awaiting' | 'replied';
export type FormReplyStatusInput = FormReplyStatus | 'none';

// ─────────────────────────────────────────────
// 公開フォームの動的フィールド payload (Phase 1+)
// getPublicForm Callable が、フォームに動的 fieldType が含まれる場合のみ返す
// ─────────────────────────────────────────────
export interface PublicFormBriefingScheduleOption {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  style: 'online' | 'onsite' | 'both';
  location?: string | null;
  capacity: number;
  bookedCount: number;
  /**
   * v3.2.18 枠を作成した担当者 uid。
   * SA リフェラルリンク (`?sa=<uid>`) 経由でフォームが開かれた場合、
   * getPublicForm はこの値で briefing_schedules をフィルタしてから返す。
   */
  assigneeUid?: string;
}

export interface PublicFormProductOption {
  productListId: string;
  listName: string;
  name: string;
  modelName: string;
  variation?: string | null;
  sku?: string | null;
  priceIncludingTax: number;
}

export interface PublicFormAssigneeOption {
  uid: string;
  displayName: string;
}

export interface PublicFormDynamicOptions {
  briefingSchedules?: PublicFormBriefingScheduleOption[];
  products?: PublicFormProductOption[];
  assignees?: PublicFormAssigneeOption[];
}

// ─────────────────────────────────────────────
// helper
// ─────────────────────────────────────────────
export function getFieldElements(elements: FormElement[]): FieldFormElement[] {
  return elements.filter((e): e is FieldFormElement => e.kind === 'field');
}

/** dealStageHint から FormCategory を1:1で導出する。 */
export function categoryFromDealStageHint(
  hint: DealStageHint | null | undefined,
): FormCategory | null {
  if (hint === 'document_requested') return 'document_download';
  if (hint === 'briefing') return 'explanation';
  if (hint === 'quote') return 'quote';
  return null;
}

/**
 * フォームのタグを返す。tag 未設定の既存フォームは 'marketing' とみなす。
 * Phase 1 リリース時の後方互換用。
 */
export function getFormTag(form: Pick<Form, 'tag'>): FormTag {
  return form.tag ?? 'marketing';
}

// ─────────────────────────────────────────────
// 構造化住所フィールド (2026-05-28 追加) のシリアライズ helper
// `billing_address` / `delivery_address` の回答値を文字列としてまとめ/分解する。
// ─────────────────────────────────────────────

export interface FormAddressParts {
  postalCode: string;
  prefecture: string;
  city: string;
  /** 町名・番地・建物 (1 行にまとめる) */
  line: string;
}

const ADDRESS_FIELD_SEP = '|';

/**
 * 構造化住所を `${postal}|${prefecture}|${city}|${line}` 形式に直列化。空成分は許容。
 *
 * ⚠️ ここで trim してはいけない (2026-08-25 修正)。
 * 公開フォームの住所入力は「parse → 編集 → serialize」を **1 打鍵ごとに往復する**
 * 制御コンポーネントのため、trim すると入力途中の末尾スペースがその場で消え、
 * 単語区切り (例: 「渋谷区神南 1-1-1」の空白) を打てなくなる。
 * 前後空白の除去は保存側 (onFormSubmissionCreated) と trimAddressParts が行う。
 */
export function serializeAddressValue(parts: FormAddressParts): string {
  return [parts.postalCode, parts.prefecture, parts.city, parts.line]
    .map((s) => (s ?? '').replace(new RegExp('\\' + ADDRESS_FIELD_SEP, 'g'), ' '))
    .join(ADDRESS_FIELD_SEP);
}

/**
 * serialized 値を分解。区切り不足は空文字列で埋める。
 * serialize と対でこちらも trim しない（入力途中の空白を保つため。上記コメント参照）。
 */
export function parseAddressValue(value: string | undefined | null): FormAddressParts {
  const raw = (value ?? '').split(ADDRESS_FIELD_SEP);
  return {
    postalCode: raw[0] ?? '',
    prefecture: raw[1] ?? '',
    city: raw[2] ?? '',
    line: raw[3] ?? '',
  };
}

/** 保存直前の正規化: 各項目の前後空白を除去する（内部の空白は保持）。 */
export function trimAddressParts(parts: FormAddressParts): FormAddressParts {
  return {
    postalCode: (parts.postalCode ?? '').trim(),
    prefecture: (parts.prefecture ?? '').trim(),
    city: (parts.city ?? '').trim(),
    line: (parts.line ?? '').trim(),
  };
}

/** すべて空（空白のみも空とみなす）なら true (未回答とみなす)。 */
export function isEmptyAddress(parts: FormAddressParts): boolean {
  const t = trimAddressParts(parts);
  return !t.postalCode && !t.prefecture && !t.city && !t.line;
}
